// App.js - Updated with Smart App Banner
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import Welcome from './pages/Welcome';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ProfileSetup from './pages/ProfileSetup';
import Profile from './pages/Profile';
import OtherUserProfile from './pages/OtherUserProfile';
import EditProfile from './pages/EditProfile';
import Settings from './pages/Settings';
import AccountDetails from './pages/AccountDetails';
import Explore from './pages/Explore';
import Invitations from './pages/Invitations';
import EventInvitation from './pages/EventInvitation';
import GuestLists from './pages/GuestLists';
import GuestListDetail from './pages/GuestListDetail';
import Communities from './pages/Communities';
import CommunityProfile from './pages/CommunityProfile';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Contact from './pages/Contact';
import Refunds from './pages/Refunds';
import { AuthProvider, useAuth } from './services/auth';
import UpgradeEventSelection from './pages/UpgradeEventSelection';
import UpgradeTiers from './pages/UpgradeTiers';
import CreateEvent from './pages/CreateEvent';
import RemoveAccount from './pages/RemoveAccount';
import SmartAppBanner from './components/SmartAppBanner';
import CoverImageTemplateUploader from './pages/CoverImageTemplateUploader';
import CoverImageTemplateBrowser from './pages/CoverImageTemplateBrowser';
import GreetingCards from './pages/GreetingCards';
import CreateGreetingCard from './pages/CreateGreetingCard';
import ViewGreetingCard from './pages/ViewGreetingCard';
import Invoices from './pages/Invoices';
import Download from './pages/Download';
import StandaloneAlbum from './pages/StandaloneAlbum';
import Notifications from './pages/Notifications';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

const RedirectToProfile = () => {
  const { user, loading } = useAuth();
  const [profile, setProfile] = React.useState(null);
  const [profileLoading, setProfileLoading] = React.useState(true);

  React.useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  const loadProfile = async () => {
    try {
      const supabase = (await import('./services/supabase')).default;
      const { data, error } = await supabase
        .from('users')
        .select('username')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  if (loading || profileLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (profile?.username) {
    return <Navigate to={`/${profile.username}`} replace />;
  }

  return <Navigate to="/profile-setup" replace />;
};

// Component to determine if showing own profile, other user's profile, or community profile
const ProfileRouter = () => {
  const { user } = useAuth();
  const { username } = useParams();
  const [isOwnProfile, setIsOwnProfile] = React.useState(false);
  const [isCommunity, setIsCommunity] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    checkProfile();
  }, [username, user]);

  const checkProfile = async () => {
    try {
      const supabase = (await import('./services/supabase')).default;

      // First check if it's a community
      const { data: communityData, error: communityError } = await supabase
        .from('communities')
        .select('username')
        .eq('username', username)
        .single();

      if (communityData && !communityError) {
        setIsCommunity(true);
        setLoading(false);
        return;
      }

      // If not a community, check if it's a user profile
      if (user) {
        const { data, error } = await supabase
          .from('users')
          .select('username')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        setIsOwnProfile(data.username === username);
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (isCommunity) {
    return <CommunityProfile />;
  }

  return isOwnProfile ? <Profile /> : <OtherUserProfile />;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        {/* ✅ Smart App Banner - Shows globally on mobile */}
        <SmartAppBanner />
        
        <Routes>
          {/* Home & Auth Routes */}
          <Route path="/" element={<Welcome />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/CoverImageTemplateUploader" element={<CoverImageTemplateUploader/>} />
          <Route path="/CoverImageTemplateBrowser" element={<CoverImageTemplateBrowser/>} />
          <Route path="/download" element={<Download />} />
          
          {/* Protected Routes */}
          <Route 
            path="/profile-setup" 
            element={
              <ProtectedRoute>
                <ProfileSetup />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/edit-profile" 
            element={
              <ProtectedRoute>
                <EditProfile />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/create-event" 
            element={
              <ProtectedRoute>
                <CreateEvent />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/edit-event/:eventId" 
            element={
              <ProtectedRoute>
                <CreateEvent />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/account-details" 
            element={
              <ProtectedRoute>
                <AccountDetails />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/explore" 
            element={
              <ProtectedRoute>
                <Explore />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/invitations"
            element={
              <ProtectedRoute>
                <Invitations />
              </ProtectedRoute>
            }
          />

          {/* Greeting Cards Routes */}
          <Route
            path="/greeting-cards"
            element={
              <ProtectedRoute>
                <GreetingCards />
              </ProtectedRoute>
            }
          />

          <Route
            path="/create-greeting-card"
            element={
              <ProtectedRoute>
                <CreateGreetingCard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/edit-greeting-card/:cardId"
            element={
              <ProtectedRoute>
                <CreateGreetingCard />
              </ProtectedRoute>
            }
          />

          {/* Guest Lists Routes */}
          <Route 
            path="/guest-lists" 
            element={
              <ProtectedRoute>
                <GuestLists />
              </ProtectedRoute>
            } 
          />

          <Route 
            path="/guest-list/:id" 
            element={
              <ProtectedRoute>
                <GuestListDetail />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/communities"
            element={
              <ProtectedRoute>
                <Communities />
              </ProtectedRoute>
            }
          />

          <Route 
            path="/upgrade" 
            element={
              <ProtectedRoute>
                <UpgradeEventSelection />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/upgrade/tiers"
            element={
              <ProtectedRoute>
                <UpgradeTiers />
              </ProtectedRoute>
            }
          />

          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <Invoices />
              </ProtectedRoute>
            }
          />

          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />

          {/* Dashboard - Redirect to user's profile */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <RedirectToProfile />
              </ProtectedRoute>
            } 
          />

          {/* Profile by username - checks if own profile or other user */}
          <Route 
            path="/:username" 
            element={
              <ProtectedRoute>
                <ProfileRouter />
              </ProtectedRoute>
            } 
          />
          
          {/* Event Invitation Routes - Public access with privacy checks inside */}
          {/* /i/:slug is what every invitation is shared as now; /invite/:slug
              stays because every link sent before that still uses it.
              React Router ranks both above the /:username catch-all. */}
          <Route path="/i/:slug" element={<EventInvitation />} />
          <Route path="/invite/:slug" element={<EventInvitation />} />
          <Route path="/event/:slug" element={<EventInvitation />} />
          <Route path="/album/:slug" element={<StandaloneAlbum />} />

          {/* Greeting Card Route - Public access with privacy checks inside */}
          <Route path="/c/:slug" element={<ViewGreetingCard />} />
          <Route path="/card/:slug" element={<ViewGreetingCard />} />
          <Route path="/cards/:slug" element={<ViewGreetingCard />} />

          {/* Legal Pages */}
          <Route path="/terms-and-conditions" element={<Terms />} />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/refunds" element={<Refunds />} />
          <Route path="/remove-account" element={<RemoveAccount />} />
          
          {/* 404 - Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;